import { getStatusLabel } from '../../../status-labels';
import { definePolicy, type Policy } from '../kernel';
import { type Action, allow, type Decision, deny } from '../types';
import { GROWER_PRODUCT_LINE_ITEMS_EDITABLE_STATUSES, type GrowerProductStatus } from './growerProduct';

/**
 * GROWER PRODUCT PRODUCTS — one rule set for the detail grid, the receiving
 * ticket and the line-item api.
 *
 *   status ∈ DRAFT..RECEIVING ─✗─▶ GROWER_PRODUCT_LINE_ITEMS_LOCKED
 *   weight column applies?    ─✗─▶ GROWER_PRODUCT_LINE_ITEM_FIELD_NOT_APPLICABLE
 *   confirmReceived: RECEIVING only
 *
 * `null` weight flags mean "not known yet" and leave the weight columns open.
 */
export type GrowerProductLineItemFacts = {
  growerProductStatus: GrowerProductStatus;
  enteredByWeight: boolean | null;
  variableWeight: boolean | null;
};

export const GROWER_PRODUCT_LINE_ITEM_FIELDS = [
  'product',
  'enteredByWeight',
  'unitsOrdered',
  'weightOrdered',
  'unitsReceived',
  'weightReceived',
  'lotGrade',
] as const;

export type GrowerProductLineItemField = (typeof GROWER_PRODUCT_LINE_ITEM_FIELDS)[number];

export const GROWER_PRODUCT_LINE_ITEM_OPS = ['clear', 'confirmReceived'] as const;
export type GrowerProductLineItemOp = (typeof GROWER_PRODUCT_LINE_ITEM_OPS)[number];

/**
 * The api's lot-floor sentence, word for word and without a trailing period, as prod sends it: a
 * MID_RECEIVING lot cannot be received below what it has already used. The same words as the
 * sales/purchase order line door (lane L6); the two can be deduplicated once both land.
 */
export const growerProductLineLotFloorMessage = (used: number, unit: 'units' | 'lbs'): string =>
  `The lot on this line item has already used ${used} ${unit}, you cannot receive less than that`;

const fieldNotApplicable = (): Decision =>
  deny('GROWER_PRODUCT_LINE_ITEM_FIELD_NOT_APPLICABLE', "Not used for this product's pricing unit.");

const decideField = (facts: GrowerProductLineItemFacts, field: GrowerProductLineItemField): Decision => {
  switch (field) {
    case 'weightOrdered':
      return facts.enteredByWeight !== false ? allow() : fieldNotApplicable();
    case 'weightReceived':
      return facts.enteredByWeight === true || facts.variableWeight !== false ? allow() : fieldNotApplicable();
    case 'product':
    case 'enteredByWeight':
    case 'unitsOrdered':
    case 'unitsReceived':
    case 'lotGrade':
      return allow();
  }
};

const fieldRule = (
  facts: GrowerProductLineItemFacts,
  action: Action<GrowerProductLineItemField, GrowerProductLineItemOp>
): Decision => (action.type === 'edit' && action.field !== undefined ? decideField(facts, action.field) : allow());

const fieldRules = {
  product: fieldRule,
  enteredByWeight: fieldRule,
  unitsOrdered: fieldRule,
  weightOrdered: fieldRule,
  unitsReceived: fieldRule,
  weightReceived: fieldRule,
  lotGrade: fieldRule,
} satisfies Record<GrowerProductLineItemField, typeof fieldRule>;

export const growerProductLineItemPolicy: Policy<
  GrowerProductLineItemFacts,
  GrowerProductLineItemField,
  GrowerProductLineItemOp
> = definePolicy<GrowerProductLineItemFacts, GrowerProductLineItemField, GrowerProductLineItemOp>({
  kind: 'grower product line',
  fields: GROWER_PRODUCT_LINE_ITEM_FIELDS,
  ops: GROWER_PRODUCT_LINE_ITEM_OPS,
  requires: {},
  entity: (facts, action) => {
    if (action.type === 'view') return allow();

    if (action.type === 'perform' && action.op === 'confirmReceived') {
      return facts.growerProductStatus === 'RECEIVING'
        ? allow()
        : deny(
            'GROWER_PRODUCT_LINE_ITEM_CONFIRM_RECEIVED_LOCKED',
            'Received quantities can be confirmed only while Receiving.'
          );
    }

    return GROWER_PRODUCT_LINE_ITEMS_EDITABLE_STATUSES.includes(facts.growerProductStatus)
      ? allow()
      : deny(
          'GROWER_PRODUCT_LINE_ITEMS_LOCKED',
          `Products are locked once the grower product is ${getStatusLabel(facts.growerProductStatus)}.`
        );
  },
  field: fieldRules,
});
